// Cc box with contact suggestions (2026-11-02). After 3 typed letters it offers the
// ticket client's active contacts whose name or email matches, leaving out anyone
// already on the ticket. Arrow keys + Enter, or a click, pick one. Typing a full
// address and pressing Enter still works exactly as before.
import { useEffect, useRef, useState } from "react";
import { getContacts } from "../api";

const MIN_CHARS = 3;
const MAX_SHOWN = 8;
let seq = 0;

export default function CcSuggest({ clientId, value, onChange, onPick, exclude = [], onKeyDown, wrapStyle, ...inputProps }) {
  const [contacts, setContacts] = useState(null);   // null = not loaded for this client yet
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const forClient = useRef(null);                   // which client the load in flight is for
  const listId = useRef(`cc-suggest-${++seq}`).current;
  const q = String(value || "").trim().toLowerCase();

  // New ticket/client: forget the old list.
  useEffect(() => { setContacts(null); forClient.current = null; }, [clientId]);

  // Load the client's contacts once, the first time 3 letters are typed.
  useEffect(() => {
    if (!clientId || q.length < MIN_CHARS || contacts || forClient.current === clientId) return;
    const cid = clientId;
    forClient.current = cid;
    getContacts(cid)
      .then(r => { if (forClient.current === cid) setContacts(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (forClient.current === cid) setContacts([]); });
  }, [clientId, q, contacts]);

  const skip = new Set(exclude.filter(Boolean).map(e => String(e).toLowerCase()));
  const list = q.length < MIN_CHARS || !contacts ? [] : contacts.filter(c => {
    const email = String(c.email || "").toLowerCase();
    if (!email || skip.has(email)) return false;
    const name = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
    return name.includes(q) || email.includes(q);
  }).slice(0, MAX_SHOWN);
  const show = open && list.length > 0;

  // A typed address (has "@") is not overridden by Enter; a typed name picks the top match.
  const listKey = list.map(c => c.id).join(",");
  useEffect(() => { setActive(listKey && !q.includes("@") ? 0 : -1); }, [listKey, q]);

  const pick = (c) => { setOpen(false); setActive(-1); onPick(String(c.email).trim().toLowerCase()); };

  const keyDown = (e) => {
    if (show) {
      if (e.key === "ArrowDown") { e.preventDefault(); setActive(i => (i + 1) % list.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setActive(i => (i <= 0 ? list.length - 1 : i - 1)); return; }
      if (e.key === "Enter" && active >= 0 && active < list.length) { e.preventDefault(); pick(list[active]); return; }
      if (e.key === "Escape") { e.preventDefault(); setOpen(false); return; }
    }
    if (onKeyDown) onKeyDown(e);
  };

  return (
    <span style={{ position: "relative", display: "inline-flex", minWidth: 0, ...wrapStyle }}>
      <input {...inputProps} value={value} autoComplete="off"
        role="combobox" aria-autocomplete="list" aria-expanded={show} aria-controls={listId}
        aria-activedescendant={show && active >= 0 ? `${listId}-${active}` : undefined}
        onChange={e => { onChange(e); setOpen(true); }}
        onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} onKeyDown={keyDown} />
      {show && (
        <div id={listId} role="listbox" aria-label="Suggested contacts" className="cv-pop"
          style={{ top: "calc(100% + 6px)", left: 0, minWidth: 260, maxWidth: 360, padding: 6 }}>
          {list.map((c, i) => {
            const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email;
            return (
              <button key={c.id} id={`${listId}-${i}`} type="button" role="option" aria-selected={i === active} className="cv-menuitem"
                onMouseDown={e => { e.preventDefault(); pick(c); }} onMouseEnter={() => setActive(i)}
                style={i === active ? { background: "var(--t-accent-softer)", color: "var(--t-text-primary)" } : undefined}>
                <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{name}</span>
                  <span style={{ fontSize: 11.5, color: "var(--t-text-faint)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.email}{/[a-z0-9]/i.test(c.job_title || "") ? ` · ${String(c.job_title).trim()}` : ""}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}
